import { useEffect, useMemo, useState } from 'react';
import {
  GameMode,
  ChoiceMessage,
  GameState,
  StatsStore,
  View,
  DailyHistory,
  Settings,
  ExperienceSnapshot,
  ExperienceGain,
  AchievementDefinition,
  AchievementSnapshot,
  Coordinates,
  RegionalMode,
} from './types';
import { LandingView } from './components/LandingView';
import { GameView } from './components/GameView';
import { StatsPanel } from './components/StatsPanel';
import { RulesPanel } from './components/RulesPanel';
import { AchievementsPanel } from './components/AchievementsPanel';
import { SettingsPanel } from './components/SettingsPanel';
import { applyTheme } from './utils/theme';
import { dailySeed, randomSeed } from './utils/prng';
import { evaluateChoice, initGame } from './logic/gameEngine';
import { getLevelProgress } from './utils/experience';
import {
  clearLocation,
  loadExperience,
  loadHistory,
  loadLocation,
  loadPinLocation,
  loadSettings,
  loadStats,
  resetAll,
  saveExperience,
  saveHistory,
  saveLocation,
  savePinLocation,
  saveSettings,
  saveStats,
} from './utils/storage';
import { City } from './types';
import { MODE_LABELS, REGIONAL_MODES, isRegionalMode } from './utils/modes';
import { getCityPoolForMode, withContinents } from './utils/cityFilters';
import { AchievementService, ChoiceAchievementEvent } from './utils/achievements';
import { AchievementToastContainer, AchievementNotice } from './components/AchievementToast';
import { selectClosestCities, distanceKm } from './utils/geo';
import './styles/globals.css';

async function fetchCities(): Promise<City[]> {
  const response = await fetch('/data/cities.json');
  if (!response.ok) throw new Error('Failed to load city dataset');
  return response.json();
}

async function fetchLocalCities(): Promise<City[]> {
  const response = await fetch('/data/small_cities.json');
  if (!response.ok) throw new Error('Failed to load local city dataset');
  return response.json();
}

export function App() {
  const [cities, setCities] = useState<City[]>([]);
  const [localCities, setLocalCities] = useState<City[] | null>(null);
  const [playerLocation, setPlayerLocation] = useState<Coordinates | null>(() => loadLocation());
  const [pinLocation, setPinLocation] = useState<Coordinates | null>(() => loadPinLocation());
  const [view, setView] = useState<View>(() => {
    if (typeof window === 'undefined') return 'landing';
    return (window.history.state?.view as View | undefined) ?? 'landing';
  });
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [stats, setStats] = useState<StatsStore>(() => loadStats());
  const [history, setHistory] = useState<DailyHistory>(() => loadHistory());
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [experience, setExperience] = useState<ExperienceSnapshot>(() => loadExperience());
  const [recentGain, setRecentGain] = useState<ExperienceGain | null>(null);
  const [customSeedInput, setCustomSeedInput] = useState('');
  const [seed, setSeed] = useState('');
  const [lastMessage, setLastMessage] = useState<ChoiceMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [achievementService] = useState(() => new AchievementService());
  const [achievementSnapshot, setAchievementSnapshot] = useState<AchievementSnapshot>(() => achievementService.getSnapshot());
  const [achievementNotices, setAchievementNotices] = useState<AchievementNotice[]>([]);
  const todayKey = useMemo(() => dailySeed().replace('daily-', ''), []);
  const todaysHistory = history[todayKey];
  const dailyLocked = Boolean(todaysHistory?.completed || todaysHistory?.seed);
  const levelProgress = useMemo(() => getLevelProgress(experience.totalExp), [experience.totalExp]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Ensure the initial history entry contains the current view for reliable navigation.
    const initialView = (window.history.state?.view as View | undefined) ?? view;
    window.history.replaceState({ view: initialView }, '', window.location.href);
    const handlePopState = (event: PopStateEvent) => {
      const nextView = (event.state?.view as View | undefined) ?? 'landing';
      setView(nextView);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Moves to a new view while keeping the browser history in sync so that
   * Back/Forward buttons restore the expected screen.
   */
  const navigate = (nextView: View, options?: { replace?: boolean }) => {
    setView(nextView);
    if (typeof window === 'undefined') return;
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (options?.replace) {
      window.history.replaceState({ view: nextView }, '', url);
    } else {
      window.history.pushState({ view: nextView }, '', url);
    }
  };

  const appendNotices = (unlocks: AchievementDefinition[]) => {
    if (!unlocks.length) return;
    if (typeof window === 'undefined') return;
    const created: AchievementNotice[] = unlocks.map((achievement) => ({
      id: `${achievement.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      achievement,
    }));
    created.forEach((notice) => {
      const timeoutId = window.setTimeout(() => {
        setAchievementNotices((prev) => prev.filter((entry) => entry.id !== notice.id));
      }, 10_000);
      return () => window.clearTimeout(timeoutId);
    });
    setAchievementNotices((prev) => [...prev, ...created]);
  };

  const applyAchievementUpdate = (unlocks: AchievementDefinition[], snapshot: AchievementSnapshot) => {
    setAchievementSnapshot({ ...snapshot });
    appendNotices(unlocks);
  };

  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  const handleToggleTheme = () => {
    setSettings((prev) => {
      const next = { ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' };
      saveSettings(next);
      return next;
    });
  };

  const handleToggleMute = () => {
    setSettings((prev) => {
      const next = { ...prev, muted: !prev.muted };
      saveSettings(next);
      return next;
    });
  };

  const handleClearLocation = () => {
    setPlayerLocation(null);
    clearLocation();
  };

  const handleResetProgress = () => {
    const reset = resetAll();
    setStats(reset.stats);
    setHistory(reset.history);
    const resetExperience = { totalExp: 0 };
    setExperience(resetExperience);
    saveExperience(resetExperience);
    setRecentGain(null);
    setAchievementSnapshot(achievementService.reset());
    setAchievementNotices([]);
  };

  useEffect(() => {
    fetchCities()
      .then((data) => setCities(withContinents(data)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Unknown error'));
  }, []);

  useEffect(() => {
    const result = achievementService.reconcileFromStats(stats, history, { silent: true });
    setAchievementSnapshot({ ...result.snapshot });
  }, [achievementService, stats, history]);

  const hasData = useMemo(() => cities.length > 0, [cities]);
  const regionalCityCounts = useMemo(() => {
    return REGIONAL_MODES.reduce<Record<RegionalMode, number>>((acc, mode) => {
      acc[mode] = getCityPoolForMode(cities, mode).length;
      return acc;
    }, {} as Record<RegionalMode, number>);
  }, [cities]);

  const requestLocationPermission = async (): Promise<Coordinates | null> => {
    if (typeof window === 'undefined') return null;
    if (playerLocation) return playerLocation;
    const accepted = window.confirm(
      'Local games use your location to find nearby cities. Do you want to share your location for this mode?',
    );
    if (!accepted) return null;
    if (!navigator.geolocation) {
      setLastMessage({ message: 'Geolocation is unavailable in this browser.', tone: 'error' });
      return null;
    }
    return new Promise<Coordinates | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = { lat: position.coords.latitude, lon: position.coords.longitude };
          setPlayerLocation(coords);
          saveLocation(coords);
          resolve(coords);
        },
        () => {
          setLastMessage({ message: 'Unable to read your location. Please try again.', tone: 'error' });
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 12_000 },
      );
    });
  };

  const startLocalGame = async () => {
    setRecentGain(null);
    const coords = playerLocation ?? (await requestLocationPermission());
    if (!coords) return;

    try {
      const dataset = localCities ?? (await fetchLocalCities());
      const annotated = localCities ? localCities : withContinents(dataset);
      if (!localCities) setLocalCities(annotated);
      const nearby = selectClosestCities(annotated, coords, 100);
      if (nearby.length < 2) {
        setLastMessage({ message: 'Not enough nearby cities to start a local game.', tone: 'error' });
        return;
      }

      const nextSeed = `local-${randomSeed()}`;
      const initialState = initGame(nearby, 'local', nextSeed, stats.local.bestStreak, 'local', coords);
      setGameState(initialState);
      setSeed(nextSeed);
      setLastMessage(null);
      navigate('game');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start local game';
      setLastMessage({ message, tone: 'error' });
    }
  };

  const startPinGame = async (coordsOverride?: Coordinates) => {
    setRecentGain(null);
    const coords = coordsOverride ?? pinLocation;
    if (!coords) {
      setLastMessage({ message: 'Choose a pin location to start a pin run.', tone: 'error' });
      return;
    }

    setPinLocation(coords);
    savePinLocation(coords);

    try {
      const dataset = localCities ?? (await fetchLocalCities());
      const annotated = localCities ? localCities : withContinents(dataset);
      if (!localCities) setLocalCities(annotated);
      const nearby = selectClosestCities(annotated, coords, 100);
      if (nearby.length < 2) {
        setLastMessage({ message: 'Not enough nearby cities to start a pin run.', tone: 'error' });
        return;
      }

      const nextSeed = `pin-${randomSeed()}`;
      const initialState = initGame(nearby, 'pin', nextSeed, stats.pin.bestStreak, 'pin', coords);
      setGameState(initialState);
      setSeed(nextSeed);
      setLastMessage(null);
      navigate('game');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start pin run';
      setLastMessage({ message, tone: 'error' });
    }
  };

  const startGame = async (mode: GameMode, seedOverride?: string, options?: { userProvidedSeed?: boolean }) => {
    if (mode === 'local') {
      await startLocalGame();
      return;
    }
    if (mode === 'pin') {
      await startPinGame();
      return;
    }
    if (!hasData) return;
    setRecentGain(null);
    const randomSeedValue = randomSeed();
    const trimmedOverride = seedOverride?.trim();
    const providedSeed =
      options?.userProvidedSeed === true ? (trimmedOverride ?? customSeedInput.trim()) : trimmedOverride ?? '';
    const userProvidedSeed = options?.userProvidedSeed ?? false;
    const nextSeed =
      mode === 'daily'
        ? dailySeed()
        : mode === 'random'
          ? providedSeed || randomSeedValue
          : `${mode}-${providedSeed || randomSeedValue}`;

    if (mode === 'daily' && dailyLocked) {
      setLastMessage({ message: "You've already played today's daily challenge.", tone: 'error' });
      return;
    }

    const runKind =
      mode === 'daily'
        ? 'daily'
        : mode === 'random'
          ? providedSeed && userProvidedSeed
            ? 'seeded'
            : 'random'
          : mode === 'top-100'
            ? 'random'
            : 'regional';

    const cityPool = getCityPoolForMode(cities, mode);
    if (cityPool.length < 2) {
      setLastMessage({
        message: 'Not enough cities available for that mode.',
        tone: 'error',
      });
      return;
    }

    const initialState = initGame(cityPool, mode, nextSeed, stats[mode].bestStreak, runKind);
    setGameState(initialState);
    setSeed(nextSeed);
    setLastMessage(null);
    if (mode === 'daily') {
      const historyKey = nextSeed.replace('daily-', '');
      const updatedHistory: DailyHistory = {
        ...history,
        [historyKey]: history[historyKey]
          ? { ...history[historyKey], seed: nextSeed, playedAt: new Date().toISOString() }
          : { seed: nextSeed, playedAt: new Date().toISOString(), completed: false },
      };
      setHistory(updatedHistory);
      saveHistory(updatedHistory);
      const achievements = achievementService.recordDailyHistory(updatedHistory, { silent: false });
      applyAchievementUpdate(achievements.unlocks, achievements.snapshot);
    }
    navigate('game');
  };

  const restartRandomWithSeed = (seedValue: string) => {
    const trimmed = seedValue.trim();
    setCustomSeedInput(seedValue);
    startGame('random', trimmed, { userProvidedSeed: Boolean(trimmed) });
  };

  const restartRandomFresh = () => {
    setCustomSeedInput('');
    startGame('random', randomSeed());
  };

  // Applies and persists experience gains after a run ends.
  const applyExperienceGain = (gained: number, mode: GameMode, streak: number) => {
    if (gained <= 0) return;
    setExperience((prev) => {
      const nextTotal = prev.totalExp + gained;
      const snapshot = { totalExp: nextTotal };
      saveExperience(snapshot);
      setRecentGain({ from: prev.totalExp, to: nextTotal, gained, mode, streak });
      return snapshot;
    });
  };

  const handleChoice = (choice: 'left' | 'right') => {
    if (!gameState) return;
    const result = evaluateChoice(gameState, choice, seed, stats);
    setGameState(result.state);
    setStats(result.stats);
    saveStats(result.stats);
    if (result.earnedExp > 0) {
      applyExperienceGain(result.earnedExp, gameState.mode, result.state.streak);
    }

    const cityDistances: number[] = [];
    if (gameState.playerLocation) {
      const coords = gameState.playerLocation;
      if (gameState.currentCity.lat !== undefined && gameState.currentCity.lon !== undefined) {
        cityDistances.push(distanceKm(coords, { lat: gameState.currentCity.lat, lon: gameState.currentCity.lon }));
      }
      if (gameState.challengerCity.lat !== undefined && gameState.challengerCity.lon !== undefined) {
        cityDistances.push(distanceKm(coords, { lat: gameState.challengerCity.lat, lon: gameState.challengerCity.lon }));
      }
    }

    const achievementEvent: ChoiceAchievementEvent = {
      correct: result.correct,
      streak: result.state.streak,
      winner: result.winner,
      loser: result.loser,
      mode: gameState.mode,
      runKind: gameState.runKind,
      populationDiff: Math.abs(result.winner.population - result.loser.population),
      closestCityDistanceKm: cityDistances.length ? Math.min(...cityDistances) : undefined,
      playerLocation: gameState.playerLocation,
    };
    const achievementOutcome = achievementService.recordChoice(achievementEvent);
    applyAchievementUpdate(achievementOutcome.unlocks, achievementOutcome.snapshot);

    if (result.historyKey) {
      const nextHistory: DailyHistory = {
        ...history,
        [result.historyKey]: {
          seed: history[result.historyKey]?.seed ?? seed,
          playedAt: history[result.historyKey]?.playedAt ?? new Date().toISOString(),
          streak: result.state.streak,
          completed: true,
        },
      };
      setHistory(nextHistory);
      saveHistory(nextHistory);
      const achievements = achievementService.recordDailyHistory(nextHistory);
      applyAchievementUpdate(achievements.unlocks, achievements.snapshot);
    }
  };

  const renderView = () => {
    if (error) {
      return <div className="notice error">{error}</div>;
    }

    if (!hasData) {
      return <div className="notice">Loading city data…</div>;
    }

    if (view === 'stats') {
      return (
        <StatsPanel
          stats={stats}
          history={history}
          onBack={() => navigate('landing')}
          totalExp={experience.totalExp}
          levelProgress={levelProgress}
        />
      );
    }

    if (view === 'rules') {
      return <RulesPanel onBack={() => navigate('landing')} />;
    }

    if (view === 'achievements') {
      return <AchievementsPanel snapshot={achievementSnapshot} onBack={() => navigate('landing')} />;
    }

    if (view === 'settings') {
      return (
        <SettingsPanel
          settings={settings}
          onToggleTheme={handleToggleTheme}
          onToggleMute={handleToggleMute}
          onClearLocation={handleClearLocation}
          onReset={handleResetProgress}
          onBack={() => navigate('landing')}
          hasSavedLocation={Boolean(playerLocation)}
        />
      );
    }

    if (view === 'game' && gameState) {
      const modeLabel = MODE_LABELS[gameState.mode] ?? 'Run';
      const canRestartSameMode =
        isRegionalMode(gameState.mode) || gameState.mode === 'local' || gameState.mode === 'pin' || gameState.mode === 'top-100';
      return (
        <GameView
          state={gameState}
          lastMessage={lastMessage}
          onChoose={handleChoice}
          onBack={() => navigate('landing')}
          onRestartRandom={restartRandomFresh}
          onRestartWithSeed={restartRandomWithSeed}
          seedValue={customSeedInput}
          onSeedChange={setCustomSeedInput}
          totalExp={experience.totalExp}
          recentGain={recentGain}
          modeLabel={modeLabel}
          onRestartSameMode={!canRestartSameMode ? undefined : () => startGame(gameState.mode)}
        />
      );
    }

    return (
      <LandingView
        onStart={startGame}
        onStartPin={startPinGame}
        onViewStats={() => navigate('stats')}
        onViewSettings={() => navigate('settings')}
        onAchievements={() => navigate('achievements')}
        onRules={() => navigate('rules')}
        seedValue={customSeedInput}
        onSeedChange={setCustomSeedInput}
        dailyLocked={dailyLocked}
        statusMessage={lastMessage}
        totalExp={experience.totalExp}
        levelProgress={levelProgress}
        regionalCityCounts={regionalCityCounts}
        hasSavedLocation={Boolean(playerLocation)}
        savedPinLocation={pinLocation}
      />
    );
  };

  return (
    <div className="app-shell">
      <div className="frame">
        <div className="frame-inner">{renderView()}</div>
      </div>
      <AchievementToastContainer
        notices={achievementNotices}
        onDismiss={(id) => setAchievementNotices((prev) => prev.filter((notice) => notice.id !== id))}
      />
    </div>
  );
}
